import { useCallback, useRef } from 'react'

/**
 * Manages an AbortController lifecycle tied to the component.
 *
 * Returns `createSignal()` — call it inside a useEffect to get a fresh
 * AbortSignal and abort any previously in-flight request. The returned
 * controller should be aborted in the effect's cleanup function so that
 * StrictMode double-invoke and unmounts both cancel in-flight requests.
 *
 * @example
 * useEffect(() => {
 *   const { signal, abort } = createSignal()
 *   fetchSomething(signal).then(...)
 *   return abort
 * }, [deps, createSignal])
 */
export function useAbortController() {
  const controllerRef = useRef<AbortController>(new AbortController())

  const createSignal = useCallback(() => {
    controllerRef.current.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    return {
      signal: controller.signal,
      abort: () => controller.abort(),
    }
  }, [])

  return { createSignal }
}
